import {
  type Branch,
  type Commit,
  type Member,
  type Repository,
  type Role,
  type Team,
  isBranch,
} from '@basehub/data/replicache/index'
import type { ClientOnlyParams, ServerOnlyParams } from '../../../tx/spec'
import { isServerAuthorized } from '../../../tx/server-auth'
import { invariant } from '@epic-web/invariant'
import { parseSpaceName } from '../helpers'
import {
  type PublicRepo,
  type PublicTeam,
  isForkableRepo,
} from '../../_public-types'
import type { AtLeast } from '@basehub/utils/tree/index'
import { isAfter } from 'date-fns'
import { getPlaygroundUserId } from '../../../playground/ids'

export const isUserIdOwner = (team: Team, userId: string) => {
  return team.members.find((m) => m.userId === userId)?.role === 'owner'
}

type IsUserRoleParams = {
  team: Team | PublicTeam
  userId: string
  roles: [Role, ...Role[]]
  repo?: Repository | PublicRepo | null
}

export const isUserRole = ({ team, userId, roles, repo }: IsUserRoleParams) => {
  const member = team.members.find((m) => m.userId === userId)
  if (!member) return false

  const hasRole = roles.includes(member.role)
  if (repo?.id && !member?.hasAllAccess) {
    return hasRole && member.repoIds?.includes(repo.id)
  }

  return hasRole
}

export const hasAccessToRepo = (
  member?: Pick<Member, 'hasAllAccess' | 'role' | 'repoIds'> | null,
  repoId?: string
): boolean => {
  if (!member) return false
  if (!repoId || member.hasAllAccess) {
    return true
  }

  if (member.repoIds?.includes(repoId)) {
    return true
  }

  return false
}

export const isGuestInRepo = ({
  repo,
  userId,
  allowVisitorInPublicRepos,
  playgroundId,
}: {
  repo: Repository | PublicRepo
  userId: string | undefined
  allowVisitorInPublicRepos: boolean
  playgroundId?: string
}) => {
  if (!repo) return false
  if (!userId && !playgroundId) return false

  let isGuest = false

  if (userId) {
    // check if user is in the repo's guest list
    if (
      'guests' in repo &&
      repo.guests?.list.some((g) => g.userId === userId)
    ) {
      isGuest = true
    }
  }

  // also allow viewers in public repos
  if (isForkableRepo(repo) && allowVisitorInPublicRepos) {
    isGuest = true
  }

  return isGuest
}

export const hasSessionOrServerAuthorization = ({
  serverSecret,
  serverOnly,
}: {
  serverSecret?: string
  serverOnly: ServerOnlyParams
}) => {
  if (serverSecret && isServerAuthorized(serverSecret)) {
    return true
  }

  if (serverOnly.session) {
    return true
  }
}

/**
 * Gatekeeps server mutations based on user role.
 * If the mutation is called from the API, the serverSecret
 * should be authorized and the role validation skipped.
 */
export const hasAccessOrThrow = ({
  serverOnly,
  clientOnly,
  serverSecret,
  allowedRoles: roles,
  guestAccess,
  strict,
}: {
  serverSecret?: string
  clientOnly: ClientOnlyParams | undefined
  serverOnly:
    | AtLeast<
        ServerOnlyParams,
        | 'team'
        | 'repo'
        | 'spaceID'
        | 'session'
        | 'resolvedRef'
        | 'isGuest'
        | 'passedFirstMutatorCheck'
      >
    | undefined
  allowedRoles: [Role, ...Role[]] | 'all'
  guestAccess: 'branch-author' | 'all' | false
  strict?: boolean
}) => {
  invariant(
    serverOnly || clientOnly,
    'serverOnly or clientOnly must be provided'
  )

  if (serverSecret && isServerAuthorized(serverSecret)) {
    invariant(serverOnly, 'serverOnly must be provided')
    serverOnly.passedFirstMutatorCheck = true
    return
  }

  const passedFirstMutatorCheck =
    serverOnly?.passedFirstMutatorCheck ?? clientOnly?.passedFirstMutatorCheck
  const team = serverOnly?.team ?? clientOnly?.team
  const repo = serverOnly?.repo ?? clientOnly?.repo
  const spaceID = serverOnly?.spaceID ?? clientOnly?.spaceID
  const isGuest = serverOnly?.isGuest ?? clientOnly?.isGuest
  const playgroundId = serverOnly?.playgroundId ?? clientOnly?.playgroundId
  const userId =
    serverOnly?.session?.userId ??
    clientOnly?.userId ??
    (playgroundId ? getPlaygroundUserId(playgroundId) : undefined)
  let branch

  if (
    serverOnly?.resolvedRef?.value &&
    isBranch(serverOnly.resolvedRef.value)
  ) {
    branch = serverOnly.resolvedRef.value
  } else if (
    clientOnly?.branchOrCommit &&
    isBranch(clientOnly.branchOrCommit)
  ) {
    branch = clientOnly.branchOrCommit
  }

  invariant(team, "team can't be null")
  invariant(spaceID, "spaceID can't be null")
  invariant(userId, "userId can't be null")

  if (!strict) {
    if (passedFirstMutatorCheck) {
      return
    }
  }

  const spaceId = parseSpaceName(spaceID)

  let isGuestAllowed = false

  if (
    repo &&
    branch &&
    spaceId.spaceType === 'repo' &&
    (isGuest || playgroundId)
  ) {
    isGuestAllowed = hasGuestAccess({
      branch,
      isGuestInHisBranch: clientOnly?.isGuestInHisBranch,
      repo,
      userId,
      guestAccess,
      playgroundId,
    })
  }

  const canAccess =
    isGuestAllowed ||
    roles === 'all' ||
    isUserRole({
      roles,
      userId,
      // If repoId is null, we're running the validation for a team mutation
      repo,
      team,
    })

  if (!canAccess) {
    throw new Error('Unauthorized - BaseHub role mismatch')
  }

  if (serverOnly) {
    serverOnly.passedFirstMutatorCheck = true
  }
  if (clientOnly) {
    clientOnly.passedFirstMutatorCheck = true
  }
}

export const hasGuestAccess = ({
  repo,
  branch,
  userId,
  guestAccess,
  isGuestInHisBranch,
  playgroundId,
}: {
  repo: Repository | PublicRepo | null
  branch: Branch | Commit | null
  isGuestInHisBranch?: boolean
  userId: string | undefined
  guestAccess: 'branch-author' | 'all' | false
  playgroundId?: string
}): boolean => {
  if (!repo) return false
  if (!userId && !playgroundId) return false

  let isGuestAllowed = false

  const userIsGuest = isGuestInRepo({
    repo,
    userId,
    allowVisitorInPublicRepos: true,
    playgroundId,
  })

  if (userIsGuest && guestAccess) {
    switch (guestAccess) {
      case 'all':
        isGuestAllowed = true
        break

      case 'branch-author':
        if (!!playgroundId) {
          isGuestAllowed = true
        } else if (branch && isBranch(branch)) {
          const branchWasCreatedAfterRepo = isAfter(
            new Date(branch.createdAt ?? 0),
            new Date(repo.createdAt ?? 0)
          )

          isGuestAllowed =
            branchWasCreatedAfterRepo && branch.authorId === userId
        } else if (!isGuestInHisBranch) {
          isGuestAllowed = false
        }
        break

      default:
        break
    }
  }

  return isGuestAllowed
}
